import { useLDflags } from '@/launchdarkly/Launchdarkly'
import { useCompanyVisualSettings } from '@/modules/companySettings/hooks/useCompanyVisualSettings'
import { TemplateCreatorLibraryItemsData } from '@/modules/templates/types/TemplateCreatorLibraryItemsData'
import { useCurrentTenantQuery } from '@/modules/tenant/hooks/useReadTenant'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionPlusTrigger,
  cn,
  Divider,
  IonIcon,
  OneLineTruncatedText,
} from '@valuecase/ui-components'
import { FC, useMemo, useState } from 'react'

interface TemplateCreatorTemplateListProps {
  tenantTemplates: TemplateCreatorLibraryItemsData
  publicTemplates: TemplateCreatorLibraryItemsData
  selectTemplate: (templateId: string) => void
  selectedTemplateId?: string
  startTemplateWithAiSelected: boolean
  setStartTemplateWithAiSelected: (selected: boolean) => void
}

export const TemplateCreatorLibrary: FC<TemplateCreatorTemplateListProps> = ({
  publicTemplates,
  tenantTemplates,
  selectTemplate,
  selectedTemplateId,
  startTemplateWithAiSelected,
  setStartTemplateWithAiSelected,
}) => {
  const { tenant } = useCurrentTenantQuery()
  const { flags } = useLDflags()
  const { companySettings } = useCompanyVisualSettings()

  // We use this flag to determine if we use any AI features - it just so happens that we made it specific to the ai writing assistant sometime ago, but now it's too late.
  const aiTemplateGeneratorEnabled = useMemo(
    () => companySettings?.aiWritingAssistantIsEnabled,
    [companySettings?.aiWritingAssistantIsEnabled],
  )
  const groupedTemplates = useMemo(() => {
    const grouped: {
      group: string | 'Other'
      templates: TemplateCreatorLibraryItemsData
    }[] = []

    for (const template of publicTemplates) {
      const group = grouped.find(
        (group) => group.group === (template.publicLibraryCategory ?? 'Other'),
      )

      if (group) {
        group.templates.push(template)
      } else {
        grouped.push({
          group: template.publicLibraryCategory ?? 'Other',
          templates: [template],
        })
      }
    }

    grouped.sort((a, b) => {
      if (a.group === 'Other') {
        // Move "Other" to the end
        return 1
      }
      if (b.group === 'Other') {
        // Keep "Other" at the end
        return -1
      }
      return a.group.localeCompare(b.group)
    })

    return tenantTemplates.length > 0
      ? [
          {
            group: `${tenant?.payload?.tenantName ?? 'Tenant'} Templates`,
            templates: tenantTemplates.filter((t) => !t.public),
          },
          ...grouped,
        ]
      : grouped
  }, [publicTemplates, tenant?.payload?.tenantName, tenantTemplates])

  const [selectedGroup, setSelectedGroup] = useState<string>('')

  const handleSelectTemplate = (templateId: string) => {
    setStartTemplateWithAiSelected(false)
    selectTemplate(templateId)
  }

  return (
    <div className={'p-3 flex flex-col gap-2'}>
      <button
        className={cn(
          'flex rounded-lg gap-2 p-4 pl-6 hover:bg-[#E9EDF7] transition-colors text-left group',
          {
            'bg-[#E9EDF7]': selectedTemplateId === '' && !startTemplateWithAiSelected,
          },
        )}
        onClick={() => {
          handleSelectTemplate('')
          setSelectedGroup('')
        }}
      >
        <IonIcon
          name={selectedTemplateId === '' ? 'add-circle' : 'add-circle-outline'}
          className={cn('w-5 h-5 text-primary-s5 group-hover:text-primary-s5 transition-colors', {
            'text-grey-s4': selectedTemplateId !== '' && !startTemplateWithAiSelected,
          })}
        />
        <span className={'text-sm font-medium'}>Start from blank</span>
      </button>

      {flags && flags['template-generator'] && aiTemplateGeneratorEnabled && (
        <button
          className={cn(
            'flex rounded-lg gap-2 p-4 pl-6 hover:bg-ai-s1-from  transition-colors text-left group items-center',
            {
              'bg-gradient-to-r from-ai-s1-from via-ai-s1-via to-ai-s1-to':
                startTemplateWithAiSelected,
            },
          )}
          onClick={() => setStartTemplateWithAiSelected(true)}
        >
          <IonIcon
            name={'c_ai-sparkles'}
            className={cn('w-5 h-5 text-ai-s3 group-hover:text-ai-4 transition-colors')}
          />
          <span className={'text-sm font-medium'}>Create with AI</span>
        </button>
      )}

      <Divider text={'Template Library'} />

      <Accordion
        type={'single'}
        className={'flex flex-col gap-2'}
        collapsible
        value={selectedGroup}
        onValueChange={setSelectedGroup}
      >
        {groupedTemplates.map(({ group, templates }) => (
          <AccordionItem value={group} key={group}>
            <AccordionPlusTrigger
              className={'text-grey-s4 font-semibold text-xs'}
              onClick={() => {
                handleSelectTemplate(templates[0].id)
              }}
            >
              {group}
            </AccordionPlusTrigger>
            <AccordionContent
              className={'flex flex-col gap-5 mt-5 mb-3 mx-8 border-l-2 border-grey-s2'}
            >
              {templates.map((t) => (
                <TemplateAccordionContentItem
                  key={t.id}
                  template={t}
                  selectTemplate={handleSelectTemplate}
                  selected={selectedTemplateId === t.id}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}

const TemplateAccordionContentItem = ({
  template,
  selected,
  selectTemplate,
}: {
  template: { id: string; title: string; emojiIcon?: string }
  selected: boolean
  selectTemplate: (templateId: string) => void
}) => {
  return (
    <button className={cn()} key={template.id} onClick={() => selectTemplate(template.id)}>
      <div
        className={cn(
          'flex items-center gap-1 pl-4 font-medium text-grey-s4 text-sm',
          'hover:text-primary-s4 transition-colors',
          {
            '-ml-0.5 border-l-2 border-primary-s5 text-primary-s5 hover:text-primary-s5': selected,
          },
        )}
      >
        <span>{template.emojiIcon ?? '🚀'}</span>
        <OneLineTruncatedText>{template.title}</OneLineTruncatedText>
      </div>
    </button>
  )
}
